import fetchAPI from '../../../lib/utils/fetch-api'
import { Poll, PollAccessState, PollAnswerItem, PollSetupRun, PollSubmission } from './types'

export async function getActivePoll(): Promise<Poll> {
  const response = await fetchAPI('/api/poll')
  return response.data
}

export async function getMySubmission(): Promise<PollAccessState> {
  const response = await fetchAPI('/api/poll/submissions/me')
  return response.data
}

export async function submitPoll(
  answers: PollAnswerItem[],
  status: 'started' | 'completed' = 'completed',
): Promise<PollSubmission> {
  const response = await fetchAPI('/api/poll/submissions', {
    method: 'POST',
    body: JSON.stringify({ answers, status }),
  })
  return response.data
}

export async function getPollSetup(): Promise<PollSetupRun | null> {
  try {
    const response = await fetchAPI('/api/poll/setup')
    return response.data
  } catch (error: any) {
    if (error?.statusCode === 404) return null
    throw error
  }
}

export async function retryPollSetup(): Promise<PollSetupRun> {
  const response = await fetchAPI('/api/poll/setup', { method: 'POST' })
  return response.data
}

export async function registerTenant(payload: Record<string, unknown>) {
  const response = await fetchAPI('/api/register', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  if (response?.errors) {
    const first = response.errors[0]
    const err: any = new Error(first?.detail || first?.message || 'Произошла ошибка')
    err.errors = response.errors
    err.statusCode = response.statusCode || 400
    throw err
  }
  if (!response?.data?.provisioning_job_id) {
    throw new Error('Не удалось запустить создание кабинета')
  }
  return response.data
}

export type VerificationChannel = 'email' | 'phone'

function throwApiErrors(response: any, fallback: string) {
  const errors = response?.errors || (response?.error ? [{ detail: response.error.message }] : null)
  if (errors) {
    const first = errors[0]
    const err: any = new Error(
      (typeof first === 'string' ? first : first?.detail || first?.message) || fallback,
    )
    err.errors = errors
    throw err
  }
}

/** Какие подтверждения обязательны при регистрации (настройка STAFF). */
export async function getRegistrationSettings(): Promise<{
  require_email: boolean
  require_phone: boolean
}> {
  try {
    const response = await fetchAPI('/api/register/settings', {}, true)
    throwApiErrors(response, 'Не удалось загрузить настройки')
    return {
      require_email: response?.data?.require_email !== false,
      require_phone: response?.data?.require_phone !== false,
    }
  } catch {
    // По умолчанию — оба подтверждения обязательны (бэкенд всё равно проверит)
    return { require_email: true, require_phone: true }
  }
}

export async function sendRegistrationCode(
  channel: VerificationChannel,
  target: string,
): Promise<{ verification_id: string; expires_in: number; resend_in: number }> {
  const response = await fetchAPI(
    '/api/register/verify/send',
    { method: 'POST', body: JSON.stringify({ channel, target }) },
    true,
  )
  throwApiErrors(response, 'Не удалось отправить код')
  return response.data
}

export async function confirmRegistrationCode(
  verificationId: string,
  code: string,
): Promise<{ token: string }> {
  const response = await fetchAPI(
    '/api/register/verify/confirm',
    { method: 'POST', body: JSON.stringify({ verification_id: verificationId, code }) },
    true,
  )
  throwApiErrors(response, 'Неверный код')
  return response.data
}

export async function getRegistrationStatus(jobId: string) {
  const response = await fetchAPI(`/api/register/status?job_id=${encodeURIComponent(jobId)}`)
  return response.data
}
